import gql from 'graphql-tag';

export const FARM_VERIFICATION_FRAGMENT = gql`
  fragment FarmVerification on FarmVerification {
    # Eligibility questions
    unrepairedDamage
    unrepairedDamageDetails
    priorFireLoss
    priorFireLossDetails
    priorLiabilityLoss
    priorLiabilityLossDetails
    chargedWithFelony
    chargedWithFelonyDetails
    foreclosureBankruptcy
    foreclosureBankruptcyDetails
    coverageDeclined
    coverageDeclinedDetails
    # Property risk factor questions
    otherResidences
    otherResidencesDetails
    dwellingsForSale
    dwellingsForSaleDetails
    unlistedStructures
    unlistedStructuresDetails
    recreationalVehicles
    recreationalVehiclesDetails
    livestockFencing
    livestockFencingDetails
    livestockEscape
    livestockEscapeDetails
    solarPanels
    solarPanelsDetails
    dogsOrExoticPets
    dogsOrExoticPetsDetails
    trampoline
    trampolineDetails
    dwellingsUnderRenovation
    dwellingsUnderRenovationDetails
    farmStructuresRentedToOthers
    farmStructuresRentedToOthersDetails
    # Operational details questions
    businessConductedOnPremises
    businessConductedOnPremisesDetails
    dayCareBusiness
    dayCareBusinessDetails
    anhydrousAmmoniaEquipment
    anhydrousAmmoniaEquipmentDetails
    processOrManufacture
    processOrManufactureDetails
    huntingOrFishingFee
    huntingOrFishingFeeDetails
    customFarming
    customFarmingDetails
    # CFM-only risk-level question (issue #32176)
    namedInsuredIsPropertyOwner
    namedInsuredIsPropertyOwnerDetails
    # Policy restrictions
    restrictions
    # Attestation
    signedFarmUWAttestation
  }
`;

export const FARM_LOCATIONS_FRAGMENT = gql`
  fragment FarmLocationsFragment on LocationDetail {
    id
    description
    isDeleted
    legalDescription {
      format
      section
      township
      townshipCoordinates
      range
      rangeCoordinates
      lot
      block
    }
    additionalLegalDescription
    locationDirections
    numberOfAcres
    address {
      address
      address2
      unit
      country
      city
      state
      zip
      county
    }
    fpc
    fpcFromLocation
    fireHydrantWithin1000ft
    risks {
      __typename
      id
      type
      description
      adjustmentFactor
      ... on FODwellingRisk {
        building {
          mortgageDetails {
            loanNumber
            mortgageHolderName
            mortgageHolderAddress {
              address
              address2
              country
              city
              state
              zip
            }
            mortgageHolderEmail
            mortgageHolderFax
            primary
            isCustom
            appliesToRelatedStructureId
          }
          sqFt
          basementSqFt
          originalBasementSqFt
          basementType
          exteriorWallType
          garageCarCapacity
          garageType
          attachedStructureSqFt
          homeLocation {
            address
            address2
            unit
            country
            city
            state
            zip
            county
          }
          homeQuality
          mobileHomeQualityGrade
          mobileHomeConfig
          imageryChoiceValue
          numFireplaces
          numStories
          numWoodBurningStoves
          numSolidFuelBurningAppliances
          residenceUsage
          numOccupants
          roofOriginal
          ownershipStatus
          yearBuilt
          typeOfHome
          numFullBathrooms
          numFamiliesInDwelling
          numHalfBathrooms
          constructionType
          roofYear
          roofType
          roofShape
          primaryHeatingSource
          secondaryHeatingSource
          numHeatingSources
          numSecondaryHeatingSources
          constructionStyle
          areasUnderConstruction
          propertyType
          swimmingPool {
            typeOfPool
            hasRemovableStepsOrLockingGate
            hasDivingBoardOrPoolSlide
            has4FtHighFenceWithSelfLatchingGate
          }
          numWellPumps
          wellPumpEstimatedValue
          wellPumpDescription
          tieDownType
          enclosedMasonryFoundation
          fullSkirtingAroundBase
          renovation {
            electricalType
            electricalYear
            electricalService
            heatingYear
            coolingYear
            plumbingYear
            heatingType
          }
          replacementCostEstimate {
            total
            itemized {
              calculatedValue
              type
              desc
              id
              category
              }
            valuationId
          }
          actualCashValueCalculation {
            total
            condition
            effectiveAge
          }
        }
        connectedHome {
          monitored
          moistureDevices
          theftPreventionDevices
          theftPreventionDeviceType
          motionDetectingDevices
          smokeDetectingDevices
          fireProtectiveDeviceType
          autoWaterShutoffDevices
          providerName
          agreeToTerms
          internetMonitoring
        }
        coverages {
          coverageA
          coverageB
          coverageC
          coverageD
          deductibleAllOther
          deductibleWindHail
          lossSettlement
          roofLossSettlement
          roofExclusion
          roofExclusionDetails
          coverageRSExtended
          deductibleEarthquake
          deductibleEarthquakeDwellingContents
          coverageWaterBackup
          repairRebuildRequirement
          cosmeticDamageExclusion
          exteriorWallSurfacingExclusion
          exteriorWallSurfacingExclusionDetails
          otherStructuresExclusion
          cosmeticDamageExclusion
          windHailExclusion
          poolLimit
          serviceLine
          roofsDamageExclusion
          exteriorDoorsWindowsDamageExclusion
          exteriorWallsDamageExclusion
          roofExclusionDetails
          roofExclusion
          otherStructuresExclusion
          otherStructuresExclusionDetails
          stormDamageExclusion
          stormDamageExclusionDetails
          replacementCostExp
          personalPropertyReplCost
          replCostMobileHome
          expandedDebrisRemoval
          expandedOrdinanceOrLaw
          resUnderConstructionLimit
          resUnderConstructionEffectiveDate
          inlandFloodInceptionDate
          inlandFloodOccurrenceLimit
          inlandFloodAggregateLimit
          inlandFloodDeductible
          inlandFloodDeductibleOverride
          inlandFloodRiskScore
          businessPropertyLimit
          businessPropertyLimitDetails
          moneyLimit
          jewelryLimit
          securitiesLimit
          motorizedVehiclesLimit
          silverwareLimit
          gunsLimit
          canEditCoverageAmount
          adjustDwellingLimitDescription
          sprinklerLeakageBuildingLimit
          sprinklerLeakageLimitDetails
          coverageMS
          hasActiveCoverageMS
          scheduleCoverages {
            coverageType
            items {
              id
              linkedItemId
              name
              category
              limit
              description
              deductible
              category
              extendedDetails {
                year
                horsepower
                serialNumber
                classification
                dimensions
                name
                description
                coverageMS
                hasActiveCoverageMS
                deductibleAllOther
                deductibleWindHail
                make
                model
                cc
                weight
                lengthFt
                milesPerHour
              }
            }
          }
        }
      }
      ... on FORentersDwellingRisk {
          building {
            propertyType
            numFamiliesInDwelling
            numSolidFuelBurningAppliances
            constructionType
            residenceUsage
            roofType
            primaryHeatingSource
            tieDownType
            enclosedMasonryFoundation
            fullSkirtingAroundBase
            swimmingPool {
              typeOfPool
              hasRemovableStepsOrLockingGate
              hasDivingBoardOrPoolSlide
              has4FtHighFenceWithSelfLatchingGate
            }
          }
          coverages {
            coverageC
            coverageD
            deductibleAllOther
            deductibleWindHail
            deductibleEarthquake
            coverageWaterBackup
            inlandFloodInceptionDate
            inlandFloodOccurrenceLimit
            inlandFloodAggregateLimit
            inlandFloodDeductible
            inlandFloodDeductibleOverride
            inlandFloodRiskScore
            tenantImprovementLimit
            moneyLimit
            jewelryLimit
            replCostMobileHome
            securitiesLimit
            motorizedVehiclesLimit
            silverwareLimit
            gunsLimit
            businessPropertyLimit
            businessPropertyLimitDetails
            poolLimit
            personalPropertyReplCost
            expandedDebrisRemoval
            serviceLine
            scheduleCoverages {
              coverageType
              items {
                id
                linkedItemId
                limit
                name
                description
                deductible
                category
                extendedDetails {
                  year
                  horsepower
                  serialNumber
                  classification
                  dimensions
                  name
                  startDate
                  endDate
                  days
                  make
                  model
                  cc
                  weight
                  lengthFt
                  milesPerHour
                  # #33935: since #33279 the SPP per-item "All peril deductible" lives here; omitting it
                  # reads back undefined and validation blocks the quote even though the value is persisted.
                  deductibleAllOther
                }
              }
            }
          }
          connectedHome {
            monitored
            moistureDevices
            theftPreventionDevices
            theftPreventionDeviceType
            motionDetectingDevices
            smokeDetectingDevices
            fireProtectiveDeviceType
            autoWaterShutoffDevices
            providerName
            agreeToTerms
            internetMonitoring
          }
        }
      ... on FOPropertyScheduledRisk {
          coverages {
            hasSnowWeightCoverage
            expandedDebrisRemoval
            deductibleEarthquake
            coverageF {
              id
              category
              limit
              description
              deductible
              deductibleWindHail
              multipleLossCoverage
              livestockCategory
              hasOpenPerils
              hasReplacementCostCoverage
              serialNumber
              make
              year
              increaseStackLimit
              quantity
              hasWinterPerils
              hasSpecialCattleEndorsement
              hasSuffocationOfLivestock
              additionalPerils
            }
            scheduleCoverages {
              coverageType
              items {
                id
                linkedItemId
                limit
                name
                description
                deductible
                category
                extendedDetails {
                  year
                  horsepower
                  serialNumber
                  classification
                  dimensions
                  kindOfAnimal
                  coinsurance
                  days
                  startDate
                  endDate
                  sex
                  breed
                  age
                  originalCost
                  name
                  make
                  model
                  cc
                  weight
                  lengthFt
                  milesPerHour
                  # #33935: since #33279 the SPP per-item "All peril deductible" lives here; omitting it
                  # reads back undefined and validation blocks the quote even though the value is persisted.
                  deductibleAllOther
                }
              }
            }
          }
        }
        ... on FOPropertyUnscheduledRisk {
          coverages {
            coverageG
            deductibleAllOther
            deductibleWindHail
            deductibleEarthquake
            lossSettlement
            coinsurance
            hasSnowWeightCoverage
            scheduleCoverages {
              coverageType
              items {
                id
                linkedItemId
                name
                limit
                description
                deductible
                category
                extendedDetails {
                  year
                  horsepower
                  serialNumber
                  classification
                  dimensions
                  kindOfAnimal
                  coinsurance
                  days
                  startDate
                  endDate
                  sex
                  breed
                  age
                  originalCost
                  name
                  make
                  model
                  cc
                  weight
                  lengthFt
                  milesPerHour
                  # #33935: since #33279 the SPP per-item "All peril deductible" lives here; omitting it
                  # reads back undefined and validation blocks the quote even though the value is persisted.
                  deductibleAllOther
                }
              }
            }
          }
        }
        ... on FOPropertyStructuresRisk {
          building {
            mortgageDetails {
              loanNumber
              mortgageHolderName
              mortgageHolderAddress {
                address
                address2
                country
                city
                state
                zip
              }
              mortgageHolderEmail
              mortgageHolderFax
              primary
              isCustom
              appliesToRelatedStructureId
            }
            buildingType
            constructionType
            buildingClass
            structureOpenSides
            occupancy
            solarMountDwelling
            solarMountOutbuilding
            solarMountGround
            numSolarPanels
            bushelCapacity
            buildingQuality
            diameter
            height
            length
            width
            sqFt
            numHeatingSources
            numSecondaryHeatingSources
            buildingType             
            numSolidFuelBurningAppliances
            solarMountDwelling
            primaryHeatingSource
            roofShape
            roofType
            roofYear
            residenceType
            yearBuilt
            replacementCostEstimate {
              total
              itemized {
                calculatedValue
                type
                desc
                id
                category
              }
            valuationId
          }
          actualCashValueCalculation {
            total
            condition
            effectiveAge
          }
          }
          coverages {
            coverageE
            contentsLimit
            deductibleAllOther
            deductibleWindHail
            deductibleEarthquake
            roofLossSettlement
            structureLossSettlement
            roofExclusionRestriction
            roofExclusionDescription
            coverageMS
            expandedOrdinanceOrLaw
            hasReplacementCostCoverage
            hasEarthquakeExteriorVeneerCoverage
            hasSnowWeightCoverage
            expandedDebrisRemoval
            specialFormCoverage
            buildingMaterialTheft
            repairRebuildRequirement
            exteriorDoorsWindowsDamageExclusion
            exteriorWallsDamageExclusion
            roofsDamageExclusion
            cosmeticDamageExclusion
            hasRoofExclusionRestriction
            canEditCoverageAmount
            acvFloorApprovedByUW
            adjustDwellingLimitDescription
            moneyLimit
            jewelryLimit
            securitiesLimit
            motorizedVehiclesLimit
            silverwareLimit
            gunsLimit
            businessPropertyLimit
            scheduleCoverages {
              coverageType
              items {
                id
                linkedItemId
                name
                limit
                description
                deductible
                category
                extendedDetails {
                  year
                  horsepower
                  serialNumber
                  classification
                  dimensions
                  kindOfAnimal
                  coinsurance
                  days
                  startDate
                  endDate
                  sex
                  breed
                  age
                  originalCost
                  name
                  make
                  model
                  cc
                  weight
                  lengthFt
                  milesPerHour
                  # #33935: since #33279 the SPP per-item "All peril deductible" lives here; omitting it
                  # reads back undefined and validation blocks the quote even though the value is persisted.
                  deductibleAllOther
                }
              }
            }
          }
        }
      }
  }
`;

export const FARM_INTERESTED_PARTY_FRAGMENT = gql`
  fragment FarmInterestedPartyFragment on FarmAdditionalPartyDetails {
    id
    name
    email
    relationship
    description
    isResident
    entityType
    fo41 {
      description
      locations
      risks
      equipment
      sppItems
      rpsItems
    }
    associatedEntities {
      entityType
      locationId
      riskId
      itemId
    }
    address {
      address
      address2
      unit
      country
      city
      state
      zip
    }
  }
`;

export const FARM_LIABILITY_COVERAGE_FRAGMENT = gql`
  fragment FarmLiabilityCoverageFragment on FarmLiabilityCoverageDetails {
    type
    coverageL
    coverageM
    annualAggregateLimit
    farmOperations
    personalInjury
    numManMonths
    careProvidedToOthers
    careProvidedToOthersDescription
    recreationalVehicleUMUIM
    watercraftUMUIM
    farmPollutionLimit
    additionalPropertiesExtensions {
      id
      type
      locationId
      riskId
      coverage
      legalDescription {
        format
        section
        township
        townshipCoordinates
        range
        rangeCoordinates
        lot
        block
      }
      additionalLegalDescription
      locationDirections
      addressDetails {
        address
        address2
        unit
        country
        county
        city
        state
        zip
      }
      description
      numFamilies
      numberOfAcres
      structureDetail
    }
    additionalAssetsExtensions {
      id
      type
      locationId
      riskId
      existingAssetId
      name
      year
      make
      model
      serialNumber
      limit
      description
      coverage
      weight
      cc
      horsepower
      classification
      mph
      length
    }
    hasExclusions
    exclusions {
      excludeActivities
      excludeLocations
      excludedActivitiesDescription
      excludedLocationsDescription
      excludedEmployeesDescription
    }
    domesticEmployees {
      id
      firstName
      lastName
    }
    customFarmWorkItems {
      type
      description
      amount
      includesSpecificActivities
    }
    customFeeding {
      id
      type
      description
      associatedLocations
    }
    businessActivities {
      type
      description
      namedInsureds
      includesSpecificActivities
    }
    businessPursuits {
      type
      amount
      namedInsureds
    }
    occupancies {
      id
      type
      description
      location
    }
    employees {
      partTime
      fullTime
      partTime40DaysOrLess
    }
  }
`;

export const FARM_FRAGMENT = gql`
  fragment FarmFragment on QuoteDetails {
    entity {
      id
      type
      name
      address {
        address
        address2
        city
        state
        zip
      }
    }
    farmLocations {
      ...FarmLocationsFragment
    }
    includeFarmLiability
    farmLiabilityCoverage {
      ...FarmLiabilityCoverageFragment
    }
    farmInterestedParties {
      ...FarmInterestedPartyFragment
    }
  }
  ${FARM_LOCATIONS_FRAGMENT}
  ${FARM_LIABILITY_COVERAGE_FRAGMENT}
  ${FARM_INTERESTED_PARTY_FRAGMENT}
`;
